/**
 * API service for managing user custom composite signs
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export interface CustomCompositeSign {
  id?: string;
  user_id?: string;
  name: string;
  components: Array<{
    id: string;
    signCode: string;
    x: number;
    y: number;
    width: number;
    height: number;
    svg: string;
    slotIndex: number;
  }>;
  canvasWidth: number;
  canvasHeight: number;
  created_at?: string;
  updated_at?: string;
  is_favorite?: boolean;
  usage_count?: number;
  last_used_at?: string;
}

/**
 * Get all custom signs for the current user
 */
export const getUserCustomSigns = async (authToken: string): Promise<CustomCompositeSign[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/custom-signs/`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
    return response.data.signs || [];
  } catch (error) {
    console.error('Error fetching custom signs:', error);
    throw error;
  }
};

/**
 * Save a new custom composite sign
 */
export const saveCustomSign = async (
  authToken: string,
  signData: Omit<CustomCompositeSign, 'id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<{ id: string }> => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/custom-signs/`,
      signData,
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return { id: response.data.id };
  } catch (error) {
    console.error('Error saving custom sign:', error);
    throw error;
  }
};

/**
 * Update an existing custom sign
 */
export const updateCustomSign = async (
  authToken: string,
  signId: string,
  updates: Partial<Omit<CustomCompositeSign, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
): Promise<void> => {
  try {
    await axios.put(
      `${API_BASE_URL}/api/custom-signs/${signId}`,
      updates,
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (error) {
    console.error('Error updating custom sign:', error);
    throw error;
  }
};

/**
 * Delete a custom sign
 */
export const deleteCustomSign = async (
  authToken: string,
  signId: string
): Promise<void> => {
  try {
    await axios.delete(`${API_BASE_URL}/api/custom-signs/${signId}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
  } catch (error) {
    console.error('Error deleting custom sign:', error);
    throw error;
  }
};

/**
 * Toggle favorite status for a custom sign
 */
export const toggleCustomSignFavorite = async (
  authToken: string,
  signId: string
): Promise<boolean> => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/custom-signs/${signId}/favorite`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      }
    );
    return response.data.is_favorite;
  } catch (error) {
    console.error('Error toggling favorite:', error);
    throw error;
  }
};

/**
 * Increment usage count for a custom sign
 */
export const incrementCustomSignUsage = async (
  authToken: string,
  signId: string
): Promise<void> => {
  try {
    await axios.post(
      `${API_BASE_URL}/api/custom-signs/${signId}/use`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      }
    );
  } catch (error) {
    console.error('Error incrementing usage:', error);
    // Don't throw - usage tracking is non-critical
  }
};

